export const CHAT_QUICK_ACTIONS = [
  { id: 'about', label: 'About', section: 'about', message: 'Tell me about the about section' },
  { id: 'services', label: 'Services', section: 'features', message: 'What services are offered?' },
  { id: 'projects', label: 'Projects', section: 'showcase', message: 'Show me project information' },
  { id: 'contact', label: 'Contact', section: 'contact', message: 'How can I get in touch?' },
  { id: 'general', label: 'Ask anything', message: 'What can you help me with?' },
]

export const WELCOME_MESSAGE = {
  type: 'assistant',
  content: `Hello! I'm the site assistant for Geo's portfolio.

I can help you navigate the site, explore projects, services, and answer common questions — no external AI required.

What would you like to explore first?`,
  actions: CHAT_QUICK_ACTIONS,
}

const matchesAny = (text, keywords) =>
  keywords.some((keyword) => text.includes(keyword))

const matchesAll = (text, keywords) =>
  keywords.every((keyword) => text.includes(keyword))

/**
 * Ordered rules: first match wins. More specific rules come first.
 */
const RESPONSE_RULES = [
  {
    match: (text) => matchesAny(text, ['hello', 'hi ', 'hi,', 'hey', 'good morning', 'good afternoon', 'good evening']),
    response: `Hello! Welcome to Geo's portfolio.

I can guide you to **About**, **Services**, **Projects**, or **Contact**, or answer questions about skills, pricing, and experience.

Use the buttons below or ask something like "show me projects" or "how do I contact?"`,
    actions: CHAT_QUICK_ACTIONS,
  },
  {
    match: (text) => matchesAny(text, ['thank', 'thanks', 'appreciate']),
    response: `You're welcome! Let me know if you want to explore another section or learn more about Geo's work.`,
    actions: CHAT_QUICK_ACTIONS,
  },
  {
    match: (text) =>
      matchesAny(text, ['who are you', 'who is geodev', 'who is geo', 'about geo', 'tell me about geo', 'your name']),
    response: `**Geo ** is a web, application, and FiveM developer building modern digital products.

He's an IT student at Notre Dame of Marbel University with experience across React, Firebase, custom dashboards, and game server development.

Visit the **[About Section](#about)** for his full profile and skills.`,
  },
  {
    match: (text) => matchesAny(text, ['contact', 'get in touch', 'reach out', 'email', 'hire', 'collaborat', 'work with']),
    response: `You can reach Geo through the **[Contact Section](#contact)**.

There you'll find a contact form for project inquiries, collaboration requests, and quotes.

Typical response topics:
• New project discussions
• FiveM server or script work
• Web and app development
• General portfolio questions`,
  },
  {
    match: (text) =>
      matchesAny(text, ['about section', 'tell me about the about']) ||
      matchesAll(text, ['about', 'section']),
    response: `The **[About Section](#about)** covers Geo's background, skills, stats, and resume download.

You'll find:
• Profile and education
• Technical skills (React, Node.js, FiveM, and more)
• Years of experience and project count
• Links to get in touch or view projects`,
  },
  {
    match: (text) =>
      matchesAny(text, ['about', 'background', 'skills', 'experience', 'resume', 'cv', 'education', 'profile']),
    response: `The **[About Section](#about)** has Geo's profile, skills, and journey.

Highlights:
• IT student at **Notre Dame of Marbel University**
• Skills in React, TypeScript, Node.js, Firebase, FiveM, and UI/UX
• Downloadable resume available on the page

Click **[About Section](#about)** to view it.`,
  },
  {
    match: (text) =>
      matchesAny(text, ['what services', 'services offered', 'what do you offer', 'what can you build']) ||
      matchesAll(text, ['services', 'section']),
    response: `The **[Services Section](#features)** lists Geo's core offerings:

• **Web Development** — React, responsive UI, performance-focused sites
• **FiveM Development** — scripts, server setup, custom resources
• **Application Development** — cross-platform apps with modern interfaces
• **Custom Solutions** — consultation and tailored builds

Click **[Services](#features)** to explore each service in detail.`,
  },
  {
    match: (text) =>
      matchesAny(text, ['service', 'development', 'fivem', 'web dev', 'application', 'app dev', 'custom solution']),
    response: `Renniel offers four main service areas in the **[Services Section](#features)**:

1. **Web Development** — modern, responsive websites
2. **FiveM Development** — gameplay systems and server tooling
3. **Application Development** — apps with clean, scalable UX
4. **Custom Solutions** — planning, integration, and bespoke builds

Visit **[Services](#features)** for full descriptions.`,
  },
  {
    match: (text) =>
      matchesAny(text, ['showcase', 'project information', 'show me project', 'portfolio work']) ||
      matchesAll(text, ['projects', 'section']),
    response: `The **[Projects Section](#showcase)** showcases completed work with previews and descriptions.

Featured types of work:
• Dashboard and admin panels
• AI chat interfaces
• POS and business tools
• FiveM and integration projects

Click **[Projects](#showcase)** to browse the full showcase.`,
  },
  {
    match: (text) =>
      matchesAny(text, ['project', 'portfolio', 'gcard', 'discord', 'dashboard', 'pos', 'chatbot', 'work sample']),
    response: `Notable projects include:

**GCard Generator** — dashboard with user management and Discord API extensions
**Discord Live Status Card** — dynamic status display via Lanyard API
**Admin Panel Dashboard** — analytics, users, and reporting
**AI Chatbot Interface** — modern conversational UI

See everything in the **[Projects Section](#showcase)**.`,
  },
  {
    match: (text) => matchesAny(text, ['gallery', 'screenshot', 'images', 'photos']),
    response: `The **[Gallery Section](#gallery)** contains visual work and project imagery.

Use it to browse screenshots and design samples from Geo's portfolio work.`,
  },
  {
    match: (text) => matchesAny(text, ['game server', 'minecraft', 'server list']),
    response: `Check the **Game Servers** section on this site for server-related content and community projects Geo has worked on.

For custom FiveM development, see **[Services](#features)** or **[Contact](#contact)**.`,
  },
  {
    match: (text) => matchesAny(text, ['price', 'pricing', 'cost', 'rate', 'fee', 'budget', 'quote', 'how much']),
    response: `Indicative pricing ranges (PHP):

**Web Development**
• Static sites: ₱3,000 – ₱15,000
• Dynamic sites: ₱15,000 – ₱20,000
• E-commerce: ₱55,000 – ₱250,000

**FiveM Development**
• Scripts: ₱1,000 – ₱15,000
• Server setup: ₱1,000 – ₱1,500

**Applications**
• Mobile / cross-platform: ₱15,000 – ₱30,000+

Final quotes depend on scope. Use **[Contact](#contact)** for a tailored estimate.`,
  },
  {
    match: (text) => matchesAny(text, ['school', 'university', 'college', 'education', 'notre dame', 'marbel', 'ndmu']),
    response: `Renniel studies **Information Technology** at **Notre Dame of Marbel University** in the Philippines.

His coursework and projects focus on web development, software engineering, and practical digital product delivery.`,
  },
  {
    match: (text) => matchesAny(text, ['age', 'how old', 'years old']),
    response: `Renniel is **20 years old** — a young developer with hands-on experience in web apps, FiveM systems, and portfolio-grade project delivery.`,
  },
  {
    match: (text) =>
      matchesAny(text, ['tech stack', 'technologies', 'programming', 'language', 'react', 'javascript', 'typescript', 'node', 'firebase', 'lua', 'python', 'php', 'laravel']),
    response: `Renniel's technical stack includes:

**Frontend:** React, JavaScript/TypeScript, HTML, CSS, Framer Motion
**Backend:** Node.js, Express, Firebase, REST APIs
**FiveM:** Lua scripting, server configuration, custom resources
**Other:** Git, Vercel, UI/UX fundamentals

See the full skill list in **[About](#about)**.`,
  },
  {
    match: (text) => matchesAny(text, ['fivem', 'gta', 'roleplay', 'rp server', 'lua script']),
    response: `Renniel builds **FiveM** experiences including custom scripts, server configuration, gameplay mechanics, and resource development.

For examples and service details, visit **[Services](#features)** and **[Projects](#showcase)**.`,
  },
  {
    match: (text) => matchesAny(text, ['website', 'site', 'navigate', 'where is', 'how do i find', 'sections', 'pages']),
    response: `This portfolio is organized into clear sections:

**[About](#about)** — profile, skills, and background
**[Services](#features)** — web, apps, FiveM, and custom work
**[Projects](#showcase)** — portfolio and case studies
**[Gallery](#gallery)** — visual work
**[Contact](#contact)** — get in touch

Tap a button below to jump to a section.`,
    actions: CHAT_QUICK_ACTIONS,
  },
  {
    match: (text) => matchesAny(text, ['help', 'what can you', 'what do you do', 'options', 'commands']),
    response: `I can answer questions about:

• Navigating this portfolio
• Services and pricing
• Projects and tech stack
• Education and background
• How to contact Geo

Try: "show projects", "services offered", "contact info", or "tech stack".`,
    actions: CHAT_QUICK_ACTIONS,
  },
]

const FALLBACK_RESPONSE = `I'm not sure I understood that, but I can help with predictable answers about this portfolio.

Try asking about:
• **Services** or **Projects**
• **Contact** and pricing
• **Skills** and **About** Geo
• How to **navigate** the site

Or use the quick buttons below.`

export function getAssistantResponse(userMessage) {
  const text = userMessage.toLowerCase().trim().replace(/\s+/g, ' ')

  if (!text) {
    return { content: 'Please type a question and I\'ll do my best to help.', actions: CHAT_QUICK_ACTIONS }
  }

  for (const rule of RESPONSE_RULES) {
    if (rule.match(text)) {
      return {
        content: rule.response,
        actions: rule.actions,
      }
    }
  }

  return {
    content: FALLBACK_RESPONSE,
    actions: CHAT_QUICK_ACTIONS,
  }
}

/** Brief delay so replies feel natural without calling an API */
export function assistantReplyDelay(message = '') {
  const base = 280
  const extra = Math.min(message.length * 4, 400)
  return base + extra
}
